import type { CodingAnswerOption } from '@seen/core/codings'
import { ExtendedObservation } from '@seen/core/models/observation'
import {
  type Condition,
  type ConditionLiteral,
  OPERATORS_BY_KIND,
  type Operand,
  type Operator,
} from '@seen/core/observation-logic/conditionSchema'
import {
  OBSERVATION_NODE,
  OBSERVATION_PANEL_NODE,
} from '@seen/core/observation-logic/documentUnits'
import type { ValueKind } from '@seen/core/observation-logic/value'
import { conditionVariables } from '@seen/core/resourceBindingRegistry'
import { panelTemplate } from '@seen/core/terminology/instruments/loincPanels'
import {
  instrumentAnswerOptions,
  instrumentItems,
  instrumentShortName,
} from '@seen/core/terminology/instruments/registry'
import type { InstrumentTemplate } from '@seen/core/terminology/instruments/types'
import type { Node as PMNode } from '@tiptap/pm/model'

// Something a condition can read, as the builder offers it.
export interface ConditionSource {
  key: string
  label: string
  group: 'Context' | 'In this note'
  operand: Operand
  kind: ValueKind
  unit?: string | null
  answerOptions?: CodingAnswerOption[]
  // Where coded answers come from when they are not carried inline.
  answerValueSet?: string | null
  // The FHIR element a context source reads, whose binding supplies coded
  // answers when none are carried inline.
  elementDefinition?: string
}

// A coded answer picked for a row: its system and display, by code.
export type PickedAnswers = Readonly<
  Record<string, { system?: string; display?: string }>
>

export type RowOperator = Operator | 'answered' | 'not answered'

export interface BuilderRow {
  type: 'row'
  source: string
  op: RowOperator
  // The value(s) as typed or picked; several for `in`.
  values: string[]
  // Coded answers picked from a value set, by code.
  picked?: PickedAnswers
}

export interface BuilderGroup {
  type: 'group'
  join: 'all' | 'any'
  rows: BuilderRow[]
}

export interface BuilderState {
  join: 'all' | 'any'
  items: (BuilderRow | BuilderGroup)[]
}

export const EMPTY_BUILDER: BuilderState = { join: 'all', items: [] }

export function contextSources(): ConditionSource[] {
  return conditionVariables().map((variable) => ({
    key: `var:${variable.alias}`,
    label: variable.label,
    group: 'Context',
    operand: { source: 'variable', id: variable.alias },
    kind: variable.kind,
    elementDefinition: variable.definition,
  }))
}

// An observation node in the note. Conditions refer to a named node by its
// name and any other by its code, where `nearest` picks the closest one.
export interface NamedNode {
  name: string | null
  code: string
  system: string | null
  label: string
  scaleType: string | null
  valueKind: string | null
  unit: string | null
  answerOptions: CodingAnswerOption[]
  answerValueSet?: string | null
}

export interface PanelSource extends NamedNode {
  isTotal: boolean
}

// An instrument's items and total, as sources ("PHQ-9 total",
// "PHQ-9 · Little interest…").
export function panelSources(template: InstrumentTemplate): PanelSource[] {
  const name = instrumentShortName(template)
  const items: PanelSource[] = instrumentItems(template).map((item) => ({
    name: null,
    code: item.coding.code ?? '',
    system: item.coding.system ?? null,
    label: `${name} · ${item.label}`,
    scaleType: item.scaleType,
    valueKind: null,
    unit: null,
    answerOptions: instrumentAnswerOptions(item),
    answerValueSet: item.answerSet ?? null,
    isTotal: false,
  }))
  const total = template.total?.coding
  return total?.code
    ? [
        ...items,
        {
          name: null,
          code: total.code,
          system: total.system ?? null,
          label: `${name} total`,
          scaleType: 'Qn',
          valueKind: null,
          unit: null,
          answerOptions: [],
          isTotal: true,
        },
      ]
    : items
}

// The other observation nodes in the note, as condition sources. A section
// skips the nodes inside it: showing it is what would answer them.
export function noteNodes(doc: PMNode, pos: number, self: PMNode): NamedNode[] {
  const found: NamedNode[] = []
  const end = pos + self.nodeSize
  doc.descendants((node, at) => {
    if (at >= pos && at < end) {
      return false
    }
    if (node.type.name === OBSERVATION_PANEL_NODE) {
      const template = panelTemplate(node.attrs)
      found.push(...(template ? panelSources(template) : []))
      return false
    }
    if (node.type.name !== OBSERVATION_NODE) {
      return true
    }
    if (node.attrs.code) {
      found.push({
        name: node.attrs.name ?? null,
        code: node.attrs.code,
        system: node.attrs.system ?? null,
        label: ExtendedObservation.getNickname(
          node.attrs.code,
          node.attrs.codingDisplay || node.attrs.display
        ),
        scaleType: node.attrs.scaleType ?? null,
        valueKind: node.attrs.valueKind ?? null,
        unit: node.attrs.unit ?? null,
        answerOptions: node.attrs.answerOptions ?? [],
        answerValueSet: node.attrs.answerValueSet ?? null,
      })
    }
    return false
  })
  return found
}

export function noteSources(nodes: readonly NamedNode[]): ConditionSource[] {
  const sources: ConditionSource[] = []
  const seen = new Set<string>()
  for (const node of nodes) {
    const key = node.name
      ? `obs:${node.name}`
      : `obs:${node.system ?? ''}|${node.code}`
    // Unnamed nodes sharing a code are one source: the nearest one answers.
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    const operand: ConditionSource['operand'] = node.name
      ? { source: 'observation', code: node.name }
      : { source: 'observation', code: node.code }
    if (!node.name && node.system) {
      operand.system = node.system
    }
    sources.push({
      key,
      label: node.name ? `${node.label} (${node.name})` : node.label,
      group: 'In this note',
      operand,
      kind: nodeKind(node),
      unit: node.unit,
      answerOptions: node.answerOptions,
      answerValueSet: node.answerValueSet ?? null,
    })
  }
  return sources
}

function nodeKind(node: NamedNode): ValueKind {
  if (node.valueKind) {
    return 'date'
  }
  switch (node.scaleType) {
    case 'Qn':
      return 'number'
    case 'Ord':
    case 'Nom':
      return 'coding'
    default:
      return 'string'
  }
}

export function operatorsFor(kind: ValueKind): RowOperator[] {
  return [...OPERATORS_BY_KIND[kind], 'answered', 'not answered']
}

function literalFor(
  source: ConditionSource,
  text: string,
  picked: PickedAnswers = {}
): ConditionLiteral {
  switch (source.kind) {
    case 'number':
      const literal: ConditionLiteral = { kind: 'number', value: Number(text) }
      if (source.unit) {
        literal.unit = source.unit
      }
      return literal
    case 'date':
      return { kind: 'date', value: text }
    case 'boolean':
      return { kind: 'boolean', value: text === 'yes' }
    case 'coding': {
      const inline = source.answerOptions?.find(
        (candidate) => candidate.code === text
      )
      const option = inline ?? picked[text]
      const literal: ConditionLiteral = { kind: 'coding', code: text }
      if (option?.system) {
        literal.system = option.system
      }
      if (option?.display) {
        literal.display = option.display
      }
      return literal
    }
    case 'string':
      return { kind: 'string', value: text }
  }
}

function literalText(literal: ConditionLiteral): string {
  switch (literal.kind) {
    case 'number':
    case 'date':
    case 'string':
      return String(literal.value)
    case 'boolean':
      return literal.value ? 'yes' : 'no'
    case 'coding':
      return literal.code
  }
}

function rowCondition(
  row: BuilderRow,
  sources: readonly ConditionSource[]
): Condition | null {
  const source = sources.find((candidate) => candidate.key === row.source)
  if (!source) {
    return null
  }
  if (row.op === 'answered' || row.op === 'not answered') {
    return {
      type: 'answered',
      operand: source.operand,
      answered: row.op === 'answered',
    }
  }
  const values = row.values.filter((value) => value.trim() !== '')
  if (!values.length) {
    return null
  }
  const literals = values.map((value) => literalFor(source, value, row.picked))
  return {
    type: 'compare',
    operand: source.operand,
    op: row.op,
    value: row.op === 'in' ? literals : (literals[0] as ConditionLiteral),
  }
}

// The condition the builder describes; rows not yet filled in are left out.
export function builderToCondition(
  state: BuilderState,
  sources: readonly ConditionSource[]
): Condition | null {
  const children = state.items.flatMap((item): Condition[] => {
    if (item.type === 'row') {
      const condition = rowCondition(item, sources)
      return condition ? [condition] : []
    }
    const rows = item.rows.flatMap((row) => {
      const condition = rowCondition(row, sources)
      return condition ? [condition] : []
    })
    return rows.length ? [{ type: item.join, conditions: rows }] : []
  })
  if (!children.length) {
    return null
  }
  if (children.length === 1 && state.items.length === 1) {
    return children[0] as Condition
  }
  return { type: state.join, conditions: children }
}

function sameOperand(a: Operand, b: Operand): boolean {
  if (a.source === 'variable' && b.source === 'variable') {
    return a.id === b.id
  }
  // A condition typed without a system still reads the node with one.
  return (
    a.source === 'observation' &&
    b.source === 'observation' &&
    a.code === b.code &&
    (!a.system || !b.system || a.system === b.system)
  )
}

function conditionRow(
  condition: Condition,
  sources: readonly ConditionSource[],
  known: PickedAnswers
): BuilderRow | null {
  if (condition.type !== 'answered' && condition.type !== 'compare') {
    return null
  }
  const operand = condition.operand
  // A variable may be written by its id or its alias; the builder offers it
  // by alias.
  const alias =
    operand.source === 'variable'
      ? conditionVariables().find(
          (variable) =>
            variable.id === operand.id || variable.alias === operand.id
        )?.alias
      : undefined
  const source = sources.find(
    (candidate) =>
      sameOperand(candidate.operand, operand) ||
      (alias !== undefined &&
        candidate.operand.source === 'variable' &&
        candidate.operand.id === alias)
  )
  if (!source) {
    return null
  }
  if (condition.type === 'answered') {
    return {
      type: 'row',
      source: source.key,
      op: condition.answered ? 'answered' : 'not answered',
      values: [],
    }
  }
  const literals = Array.isArray(condition.value)
    ? condition.value
    : [condition.value]
  // A coded answer keeps its system and display: from the literal, else
  // from what was picked before (text drops them).
  const picked: Record<string, { system?: string; display?: string }> = {}
  for (const literal of literals) {
    if (literal.kind === 'coding') {
      const before = known[literal.code]
      const system = literal.system ?? before?.system
      const display = literal.display ?? before?.display
      if (system || display) {
        const pick: PickedAnswers[string] = {}
        if (system) {
          pick.system = system
        }
        if (display) {
          pick.display = display
        }
        picked[literal.code] = pick
      }
    }
  }
  const row: BuilderRow = {
    type: 'row',
    source: source.key,
    op: condition.op,
    values: literals.map(literalText),
  }
  if (Object.keys(picked).length) {
    row.picked = picked
  }
  return row
}

// The builder form of a condition, or null when the builder cannot show it
// (a `not`, or nesting deeper than one group): the text form still can.
export function conditionToBuilder(
  condition: Condition | null | undefined,
  sources: readonly ConditionSource[],
  known: PickedAnswers = {}
): BuilderState | null {
  if (!condition) {
    return EMPTY_BUILDER
  }
  const single = conditionRow(condition, sources, known)
  if (single) {
    return { join: 'all', items: [single] }
  }
  if (condition.type !== 'all' && condition.type !== 'any') {
    return null
  }
  const items: (BuilderRow | BuilderGroup)[] = []
  for (const child of condition.conditions) {
    const row = conditionRow(child, sources, known)
    if (row) {
      items.push(row)
      continue
    }
    if (child.type !== 'all' && child.type !== 'any') {
      return null
    }
    const rows: BuilderRow[] = []
    for (const grandchild of child.conditions) {
      const nested = conditionRow(grandchild, sources, known)
      if (!nested) {
        return null
      }
      rows.push(nested)
    }
    items.push({ type: 'group', join: child.type, rows })
  }
  return { join: condition.type, items }
}

// The condition with every reference to the observation named `from` now
// reading `to`, for a rename.
export function renameInCondition(
  condition: Condition,
  from: string,
  to: string
): Condition {
  const operand = (current: Operand): Operand =>
    current.source === 'observation' && !current.system && current.code === from
      ? { ...current, code: to }
      : current
  switch (condition.type) {
    case 'all':
    case 'any':
      return {
        ...condition,
        conditions: condition.conditions.map((child) =>
          renameInCondition(child, from, to)
        ),
      }
    case 'not':
      return {
        ...condition,
        condition: renameInCondition(condition.condition, from, to),
      }
    case 'answered':
    case 'compare':
      return { ...condition, operand: operand(condition.operand) }
  }
}

const NAME_PATTERN = /^[a-z][a-z0-9_]*$/

// Why a name cannot be used, or null when it can.
export function nameProblem(
  name: string,
  others: readonly string[]
): string | null {
  if (!name) {
    return null
  }
  if (!NAME_PATTERN.test(name)) {
    return 'Use lowercase letters, digits and underscores, starting with a letter'
  }
  if (others.includes(name)) {
    return 'Another field in this note has this name'
  }
  if (
    conditionVariables().some(
      (variable) => variable.alias === name || variable.id === name
    )
  ) {
    return 'This is the name of a context variable'
  }
  return null
}

// A name for a field with none, spoken from its label and unique among the
// names already taken (`fall_risk`, then `fall_risk_2`).
export function generatedName(label: string, taken: readonly string[]): string {
  const words = label
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '_')
    .replaceAll(/^_+|_+$/g, '')
  const stem = words ? (/^[a-z]/.test(words) ? words : `q_${words}`) : 'field'
  for (let n = 1; ; n++) {
    const candidate = n === 1 ? stem : `${stem}_${n}`
    if (!nameProblem(candidate, taken)) {
      return candidate
    }
  }
}

// Every coded answer the builder's rows picked, for carrying them across a
// trip through the text form.
export function pickedAnswers(state: BuilderState): PickedAnswers {
  const rows = state.items.flatMap((item) =>
    item.type === 'row' ? [item] : item.rows
  )
  return Object.assign({}, ...rows.map((row) => row.picked ?? {}))
}
